// The non-muscle structure layer for pain capture (PAIN_TRACKING_PLAN.md
// Phase 2, D-PAIN-5/6). React-free / JSX-free on purpose (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// SEPARATE from VIEW_DATA / BODY_VIEWS on purpose (hazard H1): extending
// BODY_VIEWS with the six pain-only detail panels would break the R3 muscle-map
// check for all 53 muscles. PAIN_VIEW_DATA is its own registry keyed by
// PainView, and VIEW_DATA is reused read-only for the shared front/back/side
// muscle artwork and silhouette.
//
// front / back / side reuse the existing 220x520 canvas verbatim, so the
// hand-authored structure paths in structures{Front,Back,Side}.ts overlay the
// muscle regions with no transform. The six detail panels are Session 3-5 work;
// until then their PAIN_VIEW_DATA entries are inert placeholders so this file
// (and every consumer) typechecks standalone.

import type { HeadacheLocation, PainStructure, PainStructureKind, PainView } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { BodyRegion } from "./geometry.js";
import { mirrorPath } from "./geometry.js";
import { VIEW_DATA } from "./views.js";
import { FRONT_STRUCTURES } from "./structuresFront.js";
import { BACK_STRUCTURES } from "./structuresBack.js";
import { SIDE_STRUCTURES } from "./structuresSide.js";
import {
  HAND_PALMAR_OUTLINE,
  HAND_PALMAR_STRUCTURES,
  HAND_PALMAR_VIEWBOX,
} from "./regionsHandPalmar.js";
import {
  HAND_DORSAL_OUTLINE,
  HAND_DORSAL_STRUCTURES,
  HAND_DORSAL_VIEWBOX,
} from "./regionsHandDorsal.js";
import {
  FOOT_DORSAL_OUTLINE,
  FOOT_DORSAL_STRUCTURES,
  FOOT_DORSAL_VIEWBOX,
} from "./regionsFootDorsal.js";
import {
  FOOT_PLANTAR_OUTLINE,
  FOOT_PLANTAR_STRUCTURES,
  FOOT_PLANTAR_VIEWBOX,
} from "./regionsFootPlantar.js";
import {
  TOP_DOWN_OUTLINE,
  TOP_DOWN_STRUCTURES,
  TOP_DOWN_VIEWBOX,
} from "./regionsTopDown.js";
import { FACE_OUTLINE, FACE_STRUCTURES, FACE_VIEWBOX } from "./regionsFace.js";

/**
 * One tappable region in the pain layer. Structurally parallel to BodyRegion
 * (geometry.ts) but carries a PainStructure id and its kind instead of an
 * AnatomicalMuscle.
 */
export interface PainRegion {
  /** structure id + "-l" | "-r" | "-c". Unique within a view. */
  id: string;
  structure: PainStructure;
  kind: PainStructureKind;
  side: "left" | "right" | "center";
  /** Absolute SVG path, must start with "M". */
  d: string;
  /**
   * Nearest HEADACHE_LOCATIONS value. Set ONLY on the top-down / face panels
   * (D-PAIN-2), where a tap writes a `headache` event rather than a `symptom`;
   * Session 6 routes the saved event by this field without a lookup table.
   * Absent on every other panel.
   */
  headacheLocation?: HeadacheLocation;
}

export interface PainViewData {
  viewBox: string;
  outline: string[];
  /** Muscle regions, reused verbatim from VIEW_DATA for front/back/side;
   *  empty for the six detail panels that have no muscle artwork. */
  muscles: BodyRegion[];
  /** The non-muscle layer, drawn above the muscles. */
  structures: PainRegion[];
}

// ── Path helpers (hoisted function declarations so the structures*.ts modules,
//    which are in an import cycle with this file, can call them at module-eval
//    time before this module's own body has run) ──────────────────────────────

function fmt(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

/** A closed 4-bezier ellipse as an absolute M/C/Z path. Used for joints, bone
 *  landmarks, spine segments and visceral quadrants — a plain tap target sitting
 *  over the right anatomy. */
export function ellipsePath(cx: number, cy: number, rx: number, ry: number): string {
  const k = 0.5523; // circular bezier constant
  const ox = rx * k;
  const oy = ry * k;
  return [
    `M ${fmt(cx - rx)} ${fmt(cy)}`,
    `C ${fmt(cx - rx)} ${fmt(cy - oy)} ${fmt(cx - ox)} ${fmt(cy - ry)} ${fmt(cx)} ${fmt(cy - ry)}`,
    `C ${fmt(cx + ox)} ${fmt(cy - ry)} ${fmt(cx + rx)} ${fmt(cy - oy)} ${fmt(cx + rx)} ${fmt(cy)}`,
    `C ${fmt(cx + rx)} ${fmt(cy + oy)} ${fmt(cx + ox)} ${fmt(cy + ry)} ${fmt(cx)} ${fmt(cy + ry)}`,
    `C ${fmt(cx - ox)} ${fmt(cy + ry)} ${fmt(cx - rx)} ${fmt(cy + oy)} ${fmt(cx - rx)} ${fmt(cy)}`,
    "Z",
  ].join(" ");
}

/** The "-l" region plus its mirrored "-r" twin from one hand-authored path —
 *  the painRegions parallel of geometry.ts's pairRegions. kind is taken from
 *  PAIN_STRUCTURE_META so a region can never disagree with the taxonomy. */
export function pairPainRegions(structure: PainStructure, leftD: string): PainRegion[] {
  const kind = PAIN_STRUCTURE_META[structure].kind;
  return [
    { id: `${structure}-l`, structure, kind, side: "left", d: leftD },
    { id: `${structure}-r`, structure, kind, side: "right", d: mirrorPath(leftD) },
  ];
}

/** A single unpaired, centered region — the painRegions parallel of
 *  geometry.ts's centerRegion. */
export function centerPainRegion(structure: PainStructure, d: string): PainRegion {
  return {
    id: `${structure}-c`,
    structure,
    kind: PAIN_STRUCTURE_META[structure].kind,
    side: "center",
    d,
  };
}

// ── Registry ─────────────────────────────────────────────────────────────────

/** SEPARATE from VIEW_DATA on purpose — see this file's header and hazard H1. */
export const PAIN_VIEW_DATA: Record<PainView, PainViewData> = {
  front: {
    viewBox: VIEW_DATA.front.viewBox,
    outline: VIEW_DATA.front.outline,
    muscles: VIEW_DATA.front.regions,
    structures: FRONT_STRUCTURES,
  },
  back: {
    viewBox: VIEW_DATA.back.viewBox,
    outline: VIEW_DATA.back.outline,
    muscles: VIEW_DATA.back.regions,
    structures: BACK_STRUCTURES,
  },
  side: {
    viewBox: VIEW_DATA.side.viewBox,
    outline: VIEW_DATA.side.outline,
    muscles: VIEW_DATA.side.regions,
    structures: SIDE_STRUCTURES,
  },
  "hand-palmar": {
    viewBox: HAND_PALMAR_VIEWBOX,
    outline: HAND_PALMAR_OUTLINE,
    muscles: [],
    structures: HAND_PALMAR_STRUCTURES,
  },
  "hand-dorsal": {
    viewBox: HAND_DORSAL_VIEWBOX,
    outline: HAND_DORSAL_OUTLINE,
    muscles: [],
    structures: HAND_DORSAL_STRUCTURES,
  },
  "foot-dorsal": {
    viewBox: FOOT_DORSAL_VIEWBOX,
    outline: FOOT_DORSAL_OUTLINE,
    muscles: [],
    structures: FOOT_DORSAL_STRUCTURES,
  },
  "foot-plantar": {
    viewBox: FOOT_PLANTAR_VIEWBOX,
    outline: FOOT_PLANTAR_OUTLINE,
    muscles: [],
    structures: FOOT_PLANTAR_STRUCTURES,
  },
  "top-down": {
    viewBox: TOP_DOWN_VIEWBOX,
    outline: TOP_DOWN_OUTLINE,
    muscles: [],
    structures: TOP_DOWN_STRUCTURES,
  },
  face: {
    viewBox: FACE_VIEWBOX,
    outline: FACE_OUTLINE,
    muscles: [],
    structures: FACE_STRUCTURES,
  },
};
